import React from 'react';
import { dateString } from '../util';
import * as styles from './HistoryModal.module.scss';
import { HistoryIcon } from './HistoryIcon';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Loading,
  StructuredList,
  StructuredListItem,
  Text,
} from 'components/core';
import { Stack } from 'components/layout';
import { AuditEvent } from 'query/study/AuditEvents';
import { useAuditEvents } from 'hooks/study/useAuditEvents';
import { useCurrentReviewer } from 'hooks/reviewers/useCurrentReviewer';

type HistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  reviewId: number;
  studyId: number | string;
  studyTitle: string;
  category?: string;
  excludeNamePrefixes?: string[];
};

const HistoryModal = ({
  isOpen,
  onClose,
  reviewId,
  studyId,
  studyTitle,
  category,
  excludeNamePrefixes = [],
}: HistoryModalProps) => {
  const { data: auditEvents, isLoading, isError } = useAuditEvents(
    reviewId,
    studyId,
    { enabled: isOpen }
  );
  const { currentReviewer } = useCurrentReviewer();

  const handleClose = () => {
    onClose();
  };

  const relevantEvent = (event: AuditEvent) => {
    if (!currentReviewer) {
      return false;
    }
    if (excludeNamePrefixes.some((prefix) => event.name.startsWith(prefix))) {
      return false;
    }
    return (
      event.study_category !== category ||
      event.reviewer_id === currentReviewer.id
    );
  };

  const contentInContext = (event: AuditEvent) => {
    if (event.name.match(/\.(merged|unmerged|document)\./)) {
      return `"${event.content}"`;
    }

    return event.content;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader>History</ModalHeader>

      <ModalBody>
        <Stack spacing={5}>
          <ModalBody.Heading level={2}>{studyTitle}</ModalBody.Heading>

          {isLoading && (
            <Stack spacing={3} alignX="center">
              <Loading size="md" type="brand" />
            </Stack>
          )}

          {!isLoading && !isError && auditEvents && (
            <StructuredList listType="ol">
              {auditEvents.filter(relevantEvent).map((event) => (
                <StructuredListItem
                  key={event.id}
                  icon={<HistoryIcon auditEvent={event} />}
                >
                  <Stack className={styles.textWrapper} alignX="left">
                    <Stack spacing={1}>
                      <ModalBody.Heading level={3}>
                        {event.display_name}
                      </ModalBody.Heading>
                      {(event.reviewer_name || event.content) && (
                        <Text size="xs" variant="neutral">
                          {contentInContext(event)}
                          {event.reviewer_name && ` by ${event.reviewer_name}`}
                        </Text>
                      )}
                    </Stack>
                    <Text size="xs" variant="weak" className={styles.eventTime}>
                      {dateString(event.event_at)}
                    </Text>
                  </Stack>
                </StructuredListItem>
              ))}
            </StructuredList>
          )}
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button size="sm" type="brand" onClick={handleClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default HistoryModal;
